import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, addToSyncQueue } from '../db/shiksha-db';
import { ArrowLeft, Clock, RotateCcw, AlertTriangle, Globe } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useState, useEffect } from 'react';

export function HistoryPage() {
  const { user } = useAuth();
  const [globalLogs, setGlobalLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Teachers fetch local history
  const localHistory = useLiveQuery(async () => {
    return await db.syncHistory.orderBy('timestamp').reverse().toArray();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      const fetchGlobalLogs = async () => {
        setLoading(true);
        try {
          const idToken = await user.getIdToken();
          const apiUrl = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';
          const res = await fetch(`${apiUrl}/api/admin/history`, {
            headers: { 'Authorization': `Bearer ${idToken}` }
          });
          if (res.ok) {
            const data = await res.json();
            setGlobalLogs(data.logs || []);
          }
        } catch (e) {
          console.error("Failed to fetch global logs:", e);
        } finally {
          setLoading(false);
        }
      };
      fetchGlobalLogs();
    }
  }, [user]);

  const handleUndo = async (batchId) => {
    if (!window.confirm("Are you sure you want to undo this batch? It will delete the entries from your local device and sync the deletion to the cloud.")) {
      return;
    }

    try {
      // 1. Find and delete marks
      const marksToDelete = await db.marks.where('batchId').equals(batchId).toArray();
      for (const m of marksToDelete) {
        await addToSyncQueue('marks', m.id, 'delete', m, batchId);
        await db.marks.delete(m.id);
      }

      // 2. Find and delete attendance
      const attendanceToDelete = await db.attendance.where('batchId').equals(batchId).toArray();
      for (const a of attendanceToDelete) {
        await addToSyncQueue('attendance', a.id, 'delete', a, batchId);
        await db.attendance.delete(a.id);
      }
      
      // 3. Find and delete any pending syncQueue items for this batch (that aren't deletes we just added)
      const queuedItems = await db.syncQueue.where('batchId').equals(batchId).toArray();
      const itemsToRemove = queuedItems.filter(q => q.operation !== 'delete');
      for (const q of itemsToRemove) {
        await db.syncQueue.delete(q.id);
      }

      // 4. Remove from history
      await db.syncHistory.where('batchId').equals(batchId).delete();

    } catch (err) {
      console.error("Failed to undo batch:", err);
      alert("An error occurred while trying to undo.");
    }
  };

  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to={user?.role === 'admin' ? "/admin/dashboard" : "/teacher/dashboard"}
            className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={20} className="text-ink" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ink flex items-center gap-2">
              <Clock className="text-indigo-600" />
              Submission History
            </h1>
            <p className="text-sm text-graphite font-secondary">View and undo recent submissions from this device.</p>
          </div>
        </div>

        <div className="bg-white border border-divider rounded-xl shadow-sm overflow-hidden">
          {user?.role === 'admin' ? (
            loading ? (
              <div className="p-8 text-center text-graphite">Loading global sync logs...</div>
            ) : globalLogs.length === 0 ? (
              <div className="p-8 text-center text-graphite">
                <p>No global sync logs found.</p>
              </div>
            ) : (
              <div className="divide-y divide-divider">
                {globalLogs.map((log) => (
                  <div key={log.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-bold text-ink flex items-center gap-2">
                          <Globe size={16} className="text-blue-500" />
                          Sync by {log.email}
                        </h3>
                        <p className="text-sm text-graphite font-secondary mt-1">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-4">
                      <div className="bg-gray-50 p-2 rounded-lg text-center border border-gray-100">
                        <p className="text-xs text-graphite">Total Ops</p>
                        <p className="font-bold text-ink">{log.totalOps}</p>
                      </div>
                      <div className="bg-blue-50 p-2 rounded-lg text-center border border-blue-100">
                        <p className="text-xs text-blue-700">Marks Ops</p>
                        <p className="font-bold text-blue-900">{log.marksOps}</p>
                      </div>
                      <div className="bg-green-50 p-2 rounded-lg text-center border border-green-100">
                        <p className="text-xs text-green-700">Attendance Ops</p>
                        <p className="font-bold text-green-900">{log.attendanceOps}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            (!localHistory || localHistory.length === 0) ? (
              <div className="p-8 text-center text-graphite">
                <p>No recent submissions found on this device.</p>
              </div>
            ) : (
              <div className="divide-y divide-divider">
                {localHistory.map((entry) => (
                  <div key={entry.id} className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-gray-50 transition-colors">
                    <div>
                      <h3 className="font-bold text-ink">{entry.description}</h3>
                      <p className="text-sm text-graphite font-secondary mt-1">
                        {new Date(entry.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleUndo(entry.batchId)}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg font-medium transition-colors text-sm"
                    >
                      <RotateCcw size={16} />
                      Undo Batch
                    </button>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
        
        {user?.role !== 'admin' && (
          <div className="mt-6 flex items-start gap-3 p-4 bg-blue-50 text-blue-800 rounded-xl">
            <AlertTriangle size={20} className="shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold mb-1">How Undo Works (Offline-First)</p>
              <p>
                When you click Undo, the records are immediately removed from your local device. 
                If you are offline, a deletion request is queued and will be sent to the school's central database automatically once you regain internet access.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
